/**
 * Dynamic CSS generation for exercise theme arbitrary hex colors.
 *
 * When themes use custom hex colors via Tailwind arbitrary value syntax
 * (e.g., `bg-[#C5A059]`), those classes won't exist in the compiled CSS
 * since they come from Firestore at runtime. This utility generates the
 * necessary CSS rules and injects them into the document head.
 */

// Matches patterns like: bg-[#C5A059], text-[#1D3A29]/70, hover:bg-[#abc]/20
const ARBITRARY_HEX_RE =
  /^(hover:)?(bg|text|border|shadow)-\[#([0-9a-fA-F]{3,8})\](?:\/(\d+))?$/;

// Matches brand color patterns like: bg-foliage, hover:bg-wildfire/40, text-growth/70
const BRAND_COLOR_RE =
  /^(hover:)?(bg|text|border|shadow)-([a-z][-a-z]*)(?:\/(\d+))?$/;

/** Brand color tokens mapped to hex values (without #) for dynamic CSS generation */
const BRAND_COLOR_HEX: Record<string, string> = {
  'growth': '1D3A29',
  'branch': 'FDECD1',
  'sand': 'FCF5EA',
  'stream': 'DFEBFF',
  'cloud': 'F9F8F8',
  'twig': 'E1CD96',
  'stone': 'B2BBCB',
  'sky': '516B94',
  'clay': 'CD7857',
  'foliage': 'C5A059',
  'wildfire': '961B1B',
  'sage': '8fa89a',
  'sage-dark': '5c7066',
  'ocean': '1D3A29',
  'ocean-dark': '152b1e',
  'clay-dark': 'a65f44',
  'mist': 'e0dcd5',
  'white': 'FFFFFF',
};

const PROPERTY_MAP: Record<string, string> = {
  bg: 'background-color',
  text: 'color',
  border: 'border-color',
  shadow: '--tw-shadow-color',
};

/**
 * Convert a 3/4/6/8-char hex string to an `rgb(R G B)` or `rgb(R G B / alpha)` value.
 */
function hexToRgb(hex: string, opacityPercent?: number): string {
  // Expand shorthand: abc → aabbcc, abcd → aabbccdd
  let expanded = hex;
  if (hex.length === 3) {
    expanded = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  } else if (hex.length === 4) {
    expanded =
      hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }

  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);

  if (opacityPercent !== undefined && opacityPercent < 100) {
    const alpha = opacityPercent / 100;
    return `rgb(${r} ${g} ${b} / ${alpha})`;
  }
  return `rgb(${r} ${g} ${b})`;
}

/**
 * Escape a Tailwind class name for use as a CSS selector.
 * Characters that need escaping: [ ] # / :
 */
function escapeSelector(cls: string): string {
  return cls.replace(/([[\]#/:.])/g, '\\$1');
}

/**
 * Recursively extract all string values from an object.
 */
export function extractClassValues(obj: Record<string, any>): string[] {
  const values: string[] = [];
  for (const val of Object.values(obj)) {
    if (typeof val === 'string') {
      values.push(val);
    } else if (val && typeof val === 'object' && !Array.isArray(val)) {
      values.push(...extractClassValues(val));
    }
  }
  return values;
}

/**
 * Given an array of Tailwind class strings, find any that use arbitrary hex
 * values and generate the corresponding CSS rules.
 *
 * Brand tokens and non-hex classes are silently skipped.
 *
 * @returns A CSS string (possibly empty) ready to inject into a <style> element.
 */
export function generateDynamicCss(classValues: string[]): string {
  const seen = new Set<string>();
  const rules: string[] = [];

  for (const cls of classValues) {
    // A single value might contain multiple space-separated classes
    const parts = cls.split(/\s+/);
    for (const part of parts) {
      if (seen.has(part)) continue;
      seen.add(part);

      // Try arbitrary hex first: bg-[#C5A059], hover:text-[#1D3A29]/70
      const hexMatch = part.match(ARBITRARY_HEX_RE);
      if (hexMatch) {
        const [, hoverPrefix, type, hex, opacityStr] = hexMatch;
        const property = PROPERTY_MAP[type];
        if (!property) continue;

        const opacity = opacityStr ? parseInt(opacityStr, 10) : undefined;
        const colorValue = hexToRgb(hex, opacity);
        const selector = `.${escapeSelector(part)}`;

        if (hoverPrefix) {
          rules.push(`${selector}:hover { ${property}: ${colorValue}; }`);
        } else {
          rules.push(`${selector} { ${property}: ${colorValue}; }`);
        }
        continue;
      }

      // Try brand color: bg-foliage, hover:bg-wildfire/40, text-growth/70
      const brandMatch = part.match(BRAND_COLOR_RE);
      if (brandMatch) {
        const [, hoverPrefix, type, colorName, opacityStr] = brandMatch;
        const hex = BRAND_COLOR_HEX[colorName];
        if (!hex) continue;
        const property = PROPERTY_MAP[type];
        if (!property) continue;

        const opacity = opacityStr ? parseInt(opacityStr, 10) : undefined;
        const colorValue = hexToRgb(hex, opacity);
        const selector = `.${escapeSelector(part)}`;

        if (hoverPrefix) {
          rules.push(`${selector}:hover { ${property}: ${colorValue}; }`);
        } else {
          rules.push(`${selector} { ${property}: ${colorValue}; }`);
        }
      }
    }
  }

  return rules.join('\n');
}

/**
 * Resolve a `bg-{color}/{opacity}` Tailwind class to a CSS background-color value.
 * Supports brand tokens (e.g. `bg-sage/40`) and arbitrary hex (e.g. `bg-[#C5A059]/40`).
 * Returns null if the class can't be resolved.
 */
export function resolveBgColor(bgClass: string): string | null {
  // Brand token: bg-sage, bg-foliage/40
  const brandMatch = bgClass.match(/^bg-([a-z][-a-z]*)(?:\/(\d+))?$/);
  if (brandMatch) {
    const [, colorName, opacityStr] = brandMatch;
    const hex = BRAND_COLOR_HEX[colorName];
    if (!hex) return null;
    const opacity = opacityStr ? parseInt(opacityStr, 10) : undefined;
    return hexToRgb(hex, opacity);
  }
  // Arbitrary hex: bg-[#C5A059], bg-[#C5A059]/40
  const hexMatch = bgClass.match(/^bg-\[#([0-9a-fA-F]{3,8})\](?:\/(\d+))?$/);
  if (hexMatch) {
    const [, hex, opacityStr] = hexMatch;
    const opacity = opacityStr ? parseInt(opacityStr, 10) : undefined;
    return hexToRgb(hex, opacity);
  }
  return null;
}
